/**
 * Exam clock helpers. The client timer is display only: the server owns every deadline and
 * auto-submits at time up (spec section 3.4). These helpers turn server deadlines into what the
 * screen shows and decide what the client does around time up.
 */

interface Deadlines {
  unitDeadline: string | null;
  finalDeadline: string;
}

/** Milliseconds left in the open part (or the whole exam when it has no parts). */
export function remainingMs(d: Deadlines, clientNowMs: number, offsetMs: number): number {
  const end = Date.parse(d.unitDeadline ?? d.finalDeadline);
  return Number.isFinite(end) ? end - (clientNowMs + offsetMs) : 0;
}

export const isExpired = (remaining: number): boolean => remaining <= 0;

/** The timer turns red in the last five minutes. */
export const isLowTime = (remaining: number): boolean => remaining < 300_000;

/**
 * After time up the client saves what it has, then asks the server for the state until the
 * server has submitted (or moved to the next part). Delay grows 2 s, 4 s ... capped at 10 s.
 */
export const pollDelayMs = (tries: number): number =>
  Math.min(10_000, 2_000 * (Math.max(0, tries) + 1));

/**
 * Last resort if the server still shows the attempt as running well past the deadline (a late
 * worker tick): the client asks it to submit once. The server decides; it is safe to refuse.
 */
export const shouldRequestSubmit = (
  remaining: number,
  status: string,
  alreadyTried: boolean,
  lastPart: boolean,
): boolean => status === 'in_progress' && lastPart && !alreadyTried && remaining <= -20_000;

/** What happens once per part when its time runs out. */
type TimeUpStep = 'none' | 'flush_and_sync';

export function timeUpStep(
  expired: boolean,
  status: string,
  unitIdx: number,
  handledUnit: number | null,
): TimeUpStep {
  if (!expired || status !== 'in_progress') return 'none';
  return handledUnit === unitIdx ? 'none' : 'flush_and_sync';
}
